import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Save, Banknote, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        bank_transfer_enabled: s.bank_transfer_enabled,
        stripe_enabled: s.stripe_enabled,
        poli_enabled: s.poli_enabled,
        poli_mode: s.poli_mode,
        poli_sandbox_merchant_code: s.poli_sandbox_merchant_code,
        poli_sandbox_auth_code: s.poli_sandbox_auth_code,
        poli_live_merchant_code: s.poli_live_merchant_code,
        poli_live_auth_code: s.poli_live_auth_code,
      });
      toast.success("Payment settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const poliLive = s.poli_mode === "live";

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-extrabold mb-1">Payment Settings</h1>
      <p className="text-muted-foreground mb-6">Enable or disable each payment method shown at checkout, switch between sandbox (test) and live, and manage gateway credentials.</p>

      {/* Bank transfer */}
      <div className="rounded-xl border bg-white p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3"><Banknote className="text-accent" /><div>
            <h2 className="font-bold text-lg">Bank Transfer</h2>
            <p className="text-sm text-muted-foreground">Customer receives your bank details + reference after ordering.</p></div></div>
          <Switch data-testid="pay-bank-enabled" checked={s.bank_transfer_enabled !== false} onCheckedChange={(v) => set("bank_transfer_enabled", v)} />
        </div>
      </div>

      {/* Stripe */}
      <div className="rounded-xl border bg-white p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3"><CreditCard className="text-indigo-600" /><div>
            <h2 className="font-bold text-lg">Stripe — Card Payments</h2>
            <p className="text-sm text-muted-foreground">Secure card checkout in NZD.</p></div></div>
          <Switch data-testid="pay-stripe-enabled" checked={!!s.stripe_enabled} onCheckedChange={(v) => set("stripe_enabled", v)} />
        </div>
        <div className="mt-4 rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-900">
          <p className="font-semibold mb-1">Sandbox / Test mode is active</p>
          <p>You can test now with card <strong>4242 4242 4242 4242</strong>, any future expiry & CVC — no real charge.</p>
          <p className="mt-1">To <strong>go live</strong>, add your live Stripe key in <strong>Manage → Secrets</strong> (Live section) and re-publish. Keys are managed securely by the platform — never stored in this form.</p>
        </div>
      </div>

      {/* POLi */}
      <div className="rounded-xl border bg-white p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3"><Landmark className="text-emerald-600" /><div>
            <h2 className="font-bold text-lg">POLi Pay — Bank Payment</h2>
            <p className="text-sm text-muted-foreground">NZ online bank transfer via POLi.</p></div></div>
          <Switch data-testid="pay-poli-enabled" checked={!!s.poli_enabled} onCheckedChange={(v) => set("poli_enabled", v)} />
        </div>

        <div className="mt-4">
          <Label>Environment</Label>
          <div className="mt-1.5 inline-flex rounded-lg border bg-slate-50 p-1">
            {[["sandbox", "Sandbox (UAT / test)"], ["live", "Live"]].map(([val, label]) => (
              <button key={val} data-testid={`poli-mode-${val}`} onClick={() => set("poli_mode", val)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${s.poli_mode === val ? "bg-steel text-white" : "text-muted-foreground hover:text-steel"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className={`mt-4 grid sm:grid-cols-2 gap-4 rounded-lg p-4 ${poliLive ? "bg-red-50 border border-red-100" : "bg-emerald-50 border border-emerald-100"}`}>
          <p className="sm:col-span-2 text-sm font-semibold">{poliLive ? "LIVE credentials (real payments)" : "Sandbox / UAT credentials (safe testing)"}</p>
          {poliLive ? (
            <>
              <div><Label>Live Merchant Code</Label><Input data-testid="poli-live-mc" value={s.poli_live_merchant_code || ""} onChange={(e) => set("poli_live_merchant_code", e.target.value)} placeholder="From your POLi Merchant Console" /></div>
              <div><Label>Live Authentication Code</Label><Input data-testid="poli-live-ac" type="password" value={s.poli_live_auth_code || ""} onChange={(e) => set("poli_live_auth_code", e.target.value)} placeholder="Live auth code" /></div>
            </>
          ) : (
            <>
              <div><Label>Sandbox Merchant Code</Label><Input data-testid="poli-sandbox-mc" value={s.poli_sandbox_merchant_code || ""} onChange={(e) => set("poli_sandbox_merchant_code", e.target.value)} placeholder="UAT Merchant Code" /></div>
              <div><Label>Sandbox Authentication Code</Label><Input data-testid="poli-sandbox-ac" type="password" value={s.poli_sandbox_auth_code || ""} onChange={(e) => set("poli_sandbox_auth_code", e.target.value)} placeholder="UAT auth code" /></div>
            </>
          )}
          <p className="sm:col-span-2 text-xs text-muted-foreground">Get these from your POLi Merchant Console (polipay.co.nz). Sandbox uses the UAT gateway; Live uses the production gateway.</p>
        </div>
      </div>

      <Button data-testid="payment-settings-save" onClick={save} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
        <Save size={16} className="mr-2" /> Save Payment Settings
      </Button>
    </div>
  );
}
